import React, { useEffect, useState } from "react";
import { FaSave, FaSpinner } from "react-icons/fa";
import Toast from "../../components/Toast/Toast";
import { getGeneralSettingsAPI, updateGeneralSettingsAPI } from "../../utils/api";
import "./GeneralSetting.css";
import { getErrorMessage } from "../../constants/globalFunction";

const GeneralSetting = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  
  // Form data
  const [formData, setFormData] = useState({
    online: "1",
    cod: "1",
    tax_rates: []
  });
  

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  // Fetch general settings
  const fetchGeneralSettings = async () => {
    try {      
      const response = await getGeneralSettingsAPI();
      
      if (response?.status === 200 && response?.data) {
        const { general_setting, tax_rates } = response.data;
        setFormData({
          online: general_setting?.online?.toString() || "1",
          cod: general_setting?.cod?.toString() || "1",
          tax_rates: tax_rates || []
        });
      } else if (response?.message) {
        showToast(getErrorMessage(response?.message), "error");
      } else {
        showToast('Failed due to internal error', "error");
      }
    } catch (err) {
      console.error('Fetch error:', err);
      showToast('Failed due to internal error', "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchGeneralSettings();
  }, []);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle tax rate changes
  const handleTaxRateChange = (index, field, value) => {
    const updatedTaxRates = [...formData.tax_rates];
    updatedTaxRates[index] = {
      ...updatedTaxRates[index],
      [field]: value
    };
    
    setFormData(prev => ({
      ...prev,
      tax_rates: updatedTaxRates
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      // Validate tax rates
      const invalidTaxRates = formData.tax_rates.filter(rate => 
        parseFloat(rate?.wholesale_tax_rate) < 0 || 
        parseFloat(rate?.wholesale_tax_rate) > 100 ||
        parseFloat(rate?.retail_tax_rate) < 0 || 
        parseFloat(rate?.retail_tax_rate) > 100
      );
      
      if (invalidTaxRates.length > 0) {
        showToast('Tax rates must be between 0 and 100', 'error');
        return;
      }
      
      // Process tax rates - convert empty strings to "0"
      const processedTaxRates = formData.tax_rates.map(rate => ({
        ...rate,
        wholesale_tax_rate: rate.wholesale_tax_rate?.toString().trim() === '' ? "0" : rate.wholesale_tax_rate,
        retail_tax_rate: rate.retail_tax_rate?.toString().trim() === '' ? "0" : rate.retail_tax_rate
      }));

      const updateData = {
        online: formData.online,
        cod: formData.cod,
        tax_rates: processedTaxRates
      };
      
      const response = await updateGeneralSettingsAPI(updateData);
      
      if (response?.status === 200) {
        fetchGeneralSettings();
        showToast('General settings updated successfully', 'success');
      } else if (response?.message) {
        showToast(getErrorMessage(response?.message), "error");
      } else {
        showToast('Failed due to internal error', "error");
      }
    } catch (err) {
      console.error('Update error:', err);
      showToast('Failed due to internal error', "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <Toast toast={toast} />
        <div className="GeneralSetting-container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading general settings...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Toast toast={toast} />
      
      <div className="GeneralSetting-container">

      <div className="GeneralSetting-cards-container">
        {/* Payment Methods Card */}
        <div className="GeneralSetting-card">
          <div className="GeneralSetting-card-header">
            <h2 className="GeneralSetting-card-title">Payment Methods</h2>
          </div>
          <div className="GeneralSetting-card-content">
            <div className="GeneralSetting-payment-methods">
              <div className="GeneralSetting-payment-option">
                <label className="GeneralSetting-checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.online == "1"}
                    onChange={(e) => handleInputChange('online', e.target.checked ? "1" : "0")}
                    className="GeneralSetting-checkbox"
                  />
                  <span className="GeneralSetting-checkbox-text">Online Payment</span>
                </label>
                <p className="GeneralSetting-option-description">
                  Allow customers to pay online using credit/debit cards, UPI, net banking
                </p>
              </div>
              
              <div className="GeneralSetting-payment-option">
                <label className="GeneralSetting-checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.cod == "1"}
                    onChange={(e) => handleInputChange('cod', e.target.checked ? "1" : "0")}
                    className="GeneralSetting-checkbox"
                  />
                  <span className="GeneralSetting-checkbox-text">Cash on Delivery (COD)</span>
                </label>
                <p className="GeneralSetting-option-description">
                  Allow customers to pay in cash when the order is delivered
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tax Rates Card */}
        <div className="GeneralSetting-card">
          <div className="GeneralSetting-card-header">
            <h2 className="GeneralSetting-card-title">Tax Rates</h2>
          </div>
          <div className="GeneralSetting-card-content">
          
            {formData.tax_rates.length <= 0 ? (
              <div className="GeneralSetting-empty-state">
                <p>No countries available. Please add countries first to configure tax rates.</p>
              </div>
            ) : (
              <div className="GeneralSetting-tax-rates-single-card">
                <div className="GeneralSetting-tax-rates-header-row">
                  <div className="GeneralSetting-header-field">COUNTRY</div>
                  <div className="GeneralSetting-header-field">WHOLESALE TAX RATE</div>
                  <div className="GeneralSetting-header-field">RETAIL TAX RATE</div>
                </div>
                {formData.tax_rates.map((taxRate, index) => (
                  <div key={index} className="GeneralSetting-tax-rate-input-row">
                    <div className="GeneralSetting-input-field">
                      <div className="GeneralSetting-country-display">
                        {taxRate?.country?.name || 'Unknown Country'}
                      </div>
                    </div>
                    
                    <div className="GeneralSetting-input-field">
                      <input
                        type="text"
                        value={taxRate?.wholesale_tax_rate ?? ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Only allow numbers 0-9 and decimal point
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            handleTaxRateChange(index, 'wholesale_tax_rate', value);
                          }
                        }}
                        className="GeneralSetting-input"
                        placeholder="Wholesale Tax Rate"
                        required
                      />
                    </div>
                    
                    <div className="GeneralSetting-input-field">
                      <input
                        type="text"
                        value={taxRate?.retail_tax_rate ?? ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Only allow numbers 0-9 and decimal point
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            handleTaxRateChange(index, 'retail_tax_rate', value);
                          }
                        }}
                        className="GeneralSetting-input"
                        placeholder="Retail Tax Rate"
                        required
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <form onSubmit={handleSubmit} className="GeneralSetting-form">
        <div className="GeneralSetting-actions">
          <button
            type="submit"
            disabled={saving}
            className="GeneralSetting-save-btn"
          >
            {saving ? (
              <>
                Saving...
              </>
            ) : (
              <>
                Save
              </>
            )}
          </button>
        </div>
      </form>

    </div>
    </>
  );
};

export default GeneralSetting;
